<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-clock-o"></i>
          {{
            `${$label("common.free_times", "Free times")}: ${CalendarApi &&
              CalendarApi.view &&
              CalendarApi.view.title}`
          }}
        </h1>
      </div>
    </div>
    <div class="row calendar-container">
      <span id="calendar-label">Display off</span>
      <date-picker
        v-model="selectedDate"
        :format="dateFormat"
        :readonly="true"
        :placeholder="dateFormat"
        class="input"
        id="timetable-selected-date"
      >
      </date-picker>
      <FullCalendar
        id="timetable-calendar"
        ref="calendar"
        class="calendar"
        :options="calendarOptions"
        :key="componentKey"
      >
        <template v-slot:eventContent="arg">
          <b>{{ arg.timeText }}</b>
          <i>{{ arg.event.title }}</i>
        </template>
      </FullCalendar>
      <div class="row calendar-legend-container">
        <div class="col-xs-3">
          <div class="calendar-legend-single calendar-legend text-center">
            Einzel
          </div>
          <div
            class="calendar-legend-reserved-single calendar-legend text-center"
          >
            Reservierung Einzel
          </div>
        </div>
        <div class="col-xs-3">
          <div class="calendar-legend-subscription calendar-legend text-center">
            Abo
          </div>
          <div
            class="calendar-legend-reserved-subscription calendar-legend text-center"
          >
            Reservierung Abo
          </div>
        </div>
        <div class="col-xs-3">
          <div class="calendar-legend-course calendar-legend text-center">
            Kurs
          </div>
          <div class="calendar-legend-holiday calendar-legend text-center">
            Feiertag
          </div>
        </div>
        <div class="col-xs-3">
          <div class="calendar-legend-timeframe calendar-legend text-center">
            Geschlossen / Gesperrt
          </div>
          <div class="text-center" style="padding-top: 2px;">
            <a id="ccTrue" class="custom-colors">Kundenfarben aktivieren</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/api.js";

import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import resourceTimegridPlugin from "@fullcalendar/resource-timegrid";
import DateTime from "luxon/src/datetime";

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/de";

let eventGuid = 0;
let todayStr = new Date().toISOString().replace(/T.*$/, ""); // YYYY-MM-DD of today

const INITIAL_EVENTS = [
  {
    id: createEventId(),
    title: "All-day event",
    start: todayStr
  },
  {
    id: createEventId(),
    title: "Timed event",
    start: todayStr + "T12:00:00"
  }
];

function createEventId() {
  return String(eventGuid++);
}

export default {
  components: {
    FullCalendar, // make the <FullCalendar> tag available
    DatePicker
  },

  data: function() {
    return {
      componentKey: 0,
      facilities: [],
      loading: false,
      selectedDate: new Date(),
      dateFormat: "DD.MM.YYYY",
      CalendarApi: null,
      calendarOptions: {
        schedulerLicenseKey: "CC-Attribution-NonCommercial-NoDerivatives",
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick
          luxonPlugin,
          resourceTimelinePlugin,
          resourceTimegridPlugin
        ],
        headerToolbar: {
          left: "prev today next",
          right: ""
        },
        datesAboveResources: true,
        resourceGroupField: "",
        resources: [],
        height: "auto",
        allDaySlot: false,
        firstDay: 1,
        dayHeaders: true,
        dayHeaderFormat: "EEE d.M",
        datesSet: this.handleChangeDate,
        slotMinTime: "07:00:00",
        slotMaxTime: "23:00:00",
        slotMinWidth: "100",
        slotLabelFormat: "HH",
        slotLabelContent: this.handleSlotLabelContent,
        eventTimeFormat: "HH:mm",
        slotDuration: "01:00",
        initialView: "resourceTimeGridWeek",
        titleFormat: "{{dd.LL}}.yyyy",
        initialEvents: INITIAL_EVENTS, // alternatively, use the `events` setting to fetch from a feed
        editable: true,
        droppable: true,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: true,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventsSet: this.handleEvents,
        eventDragStart: this.handleEventDragStart,
        eventDrop: this.handleEventDrop,
        eventReceive: this.handleEventDrop,
        resourceAreaHeaderContent: "Location",
        weekNumbers: true,
        weekNumberFormat: this.weekNumberFormat
        /* you can update a remote database when these fire:
            eventAdd:
            eventChange:
            eventRemove:
            */
      },
      currentEvents: []
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getFacilities(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.getFacilities(() => {
      //
    });
    next();
  },

  mounted() {
    document
      .getElementsByClassName("fc-toolbar-chunk")[0]
      .prepend(document.getElementById("timetable-selected-date"));

    document
      .getElementsByClassName("fc-toolbar-chunk")[0]
      .prepend(document.getElementById("calendar-label"));

    this.CalendarApi = this.$refs.calendar.$options.calendar;
  },

  methods: {
    getFacilities(successCallback) {
      const endpoint = this.$partnerEndpoint("facilities/simplified");

      Api()
        .get(endpoint)
        .then(response => {
          this.facilities = response.data.data;
          response.data.data.map(facility => {
            facility.venues &&
              facility.venues.map(venue => {
                this.calendarOptions.resources.push({
                  id: venue.id,
                  title: `${facility.name} - ${venue.name}`
                });
              });
          });
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    handleDateSelect(selectInfo) {
      let title = prompt("Please enter a new title for your event");
      let calendarApi = selectInfo.view.calendar;

      calendarApi.unselect(); // clear date selection

      if (title) {
        calendarApi.addEvent({
          id: createEventId(),
          title,
          start: selectInfo.startStr,
          end: selectInfo.endStr,
          allDay: selectInfo.allDay,
          resourceId: selectInfo.resource.id
        });
      }
    },

    handleEventClick(clickInfo) {
      if (
        confirm(
          `Are you sure you want to delete the event "${clickInfo.event.title}"`
        )
      ) {
        clickInfo.event.remove();
      }
    },

    handleEvents(events) {
      this.currentEvents = events;
    },

    handleEventDragStart() {
      this.CalendarApi.setOption("snapDuration", "00:05:00");
    },

    handleEventDrop() {
      this.CalendarApi.setOption("snapDuration", "00:30:00");
    },

    handleChangeDate(dateInfo) {
      this.selectedDate = dateInfo.start;
    },

    handleSlotLabelContent(arg) {
      return isNaN(parseInt(arg.text))
        ? arg.text
        : `${arg.text} - ${parseInt(arg.text) < 9 ? "0" : ""}${parseInt(
            arg.text
          ) + 1}`;
    },

    weekNumberFormat(value) {
      return `KW ${
        DateTime.local(
          value.date.array[0],
          value.date.array[1] + 1,
          value.date.array[2]
        ).weekNumber
      }`;
    },

    forceRerender() {
      this.componentKey += 1;
    }
  },

  watch: {
    selectedDate(newDate) {
      newDate && this.CalendarApi.gotoDate(newDate);
    }
  }
};
</script>

<style>
.fc-toolbar-title {
  margin: 0;
  font-size: 16px;
}

ul {
  margin: 0;
  padding: 0 0 0 1.5em;
}

li {
  margin: 1.5em 0;
  padding: 0;
}

/* used for event dates/times */
b {
  margin-right: 3px;
}

.calendar-container {
  flex-grow: 1;
  padding: 0 2rem;
}

#calendar-label {
  display: flex;
  align-items: center;
}

#timetable-selected-date {
  width: 150px !important;
}

#timetable-selected-date.mx-datepicker .mx-icon-calendar {
  height: 100%;
  width: 32px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0088cc;
}

#timetable-selected-date.mx-datepicker .mx-icon-calendar svg {
  fill: white;
}
/* the calendar root */
#timetable-calendar .fc-timeline-slot.fc-timeline-slot-label {
  background-color: #0088cc;
}

#timetable-calendar .fc-timeline-slot.fc-timeline-slot-label a {
  color: white;
  margin: auto;
}

.fc {
  flex-grow: 1;
}

.fc .fc-toolbar {
  align-items: flex-start;
}

.fc .fc-toolbar-title {
  font-size: 1.2em;
}

.fc .fc-toolbar-chunk {
  display: flex;
}

.fc .fc-button {
  border: none;
  text-transform: capitalize;
}

.fc-button:hover,
.fc-button:active,
.fc-button-active {
  opacity: 0.65;
}

.fc-button:hover,
.fc-button:active,
.fc-button:focus {
  border: none;
  outline: none;
}

.fc-button:focus {
  box-shadow: none !important;
}

.fc-button.fc-button-primary,
.fc-button.fc-button-primary:hover,
.fc-button.fc-button-primary:active {
  background-color: #0088cc !important;
}

.fc-prev-button,
.fc-next-button {
  padding: 0.4em 0.9em !important;
}
</style>
